import React, { createContext, useContext, useEffect, useState } from 'react';
import styled from 'styled-components';

import CheckboxInput from '../CheckboxInput';
import Label from '../Label';
import Text from '../Text';

export interface CheckboxInputProps {
  name: string;
  label?: string;
  disabled?: boolean;
}

export interface CheckboxContextProps {
  selected: CheckboxInputProps[];
  handleSelectChange?: (checkbox: CheckboxInputProps) => void;
}

export interface CheckboxGroupProps {
  children?: React.ReactNode;
  initialSelected: CheckboxInputProps[];
  onChange: (value: CheckboxInputProps[]) => void;
  label?: string;
  errorMessage?: string;
}

const ErrorMessage = styled(Text)`
  margin: 0;
  margin-top: 4px;
  font-size: 12px;
  color: ${({ theme }) => theme.errorColor};
`;

const CheckboxContext = createContext<CheckboxContextProps>({
  selected: [],
});

const Checkbox = ({ name, label, disabled }: CheckboxInputProps) => {
  const { selected, handleSelectChange }: CheckboxContextProps = useContext(CheckboxContext);
  const isSelected = selected.some((checkbox) => checkbox.name === name);

  const handleChange = () => {
    if (!disabled) {
      handleSelectChange && handleSelectChange({ name, label });
    }
  };

  return (
    <CheckboxInput
      checked={isSelected}
      id={name}
      name={name}
      label={label}
      disabled={disabled}
      onChange={handleChange}
    />
  );
};

const CheckboxGroup = ({
  children,
  initialSelected,
  label,
  onChange,
  errorMessage,
}: CheckboxGroupProps) => {
  const [selected, setSelected] = useState<CheckboxInputProps[] | []>([]);

  useEffect(() => {
    if (initialSelected.length) {
      setSelected(initialSelected);
    }
  }, [initialSelected]);

  const handleSelectChange = (checkbox: CheckboxInputProps) => {
    const isSelected = selected.some(
      (selected: CheckboxInputProps) => selected.name === checkbox.name
    );

    const newSelection: CheckboxInputProps[] | [] = isSelected
      ? selected!.filter((item: CheckboxInputProps) => item.name !== checkbox.name)
      : [...selected, checkbox];
    setSelected(newSelection);
    onChange(newSelection);
  };

  return (
    <CheckboxContext.Provider value={{ selected, handleSelectChange }}>
      {label && <Label>{label}</Label>}
      {children}
      {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
    </CheckboxContext.Provider>
  );
};

CheckboxGroup.Input = Checkbox;

export default CheckboxGroup;
